import React, { useMemo, useState } from "react";
import { toast, ModalV2, Button, Text, Flex, Banner } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useCustomFields } from "../../hooks/useCustomFields";
import {
  CustomFieldType,
  CustomFieldEntityType,
  CreateCustomFieldInput,
  CustomField,
  CustomFieldOption,
  PrefillOption,
  Section,
} from "../../types";
import CustomFieldModal from "../CustomFieldModal";
import * as styles from "../styles_vanilla.css";

// Extended interface for edit form that preserves IDs
interface EditCustomFieldFormInput extends Omit<CreateCustomFieldInput, "options"> {
  options?: CustomFieldOption[]; // Include IDs for existing options
}

interface Props {
  show: boolean;
  onHide: () => void;
  field: CustomField | null;
  sections: Section[] | null;
}

const EditCustomFieldModal: React.FC<Props> = ({ show, onHide, field, sections }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    name?: string;
    enumOptionsUpsert: Array<{ id?: string; label: string }>;
    enumOptionsDelete: Array<{ id: string }>;
    sectionId?: string;
  } | null>(null);
  const { editField } = useCustomFields();

  // Determine which section this field currently belongs to
  const currentSectionId = useMemo(() => {
    if (!field || !sections) return;

    const fieldSection = sections.find((section) =>
      section.extensionFieldIds.includes(field.id),
    );

    return fieldSection?.id;
  }, [field, sections]);

  // Create initial values from the field data (preserving IDs for existing options)
  const initialValues: EditCustomFieldFormInput = {
    name: field?.name || "",
    type: field?.type || CustomFieldType.TEXT,
    entityType: field?.entityType || CustomFieldEntityType.ENTITY,
    options: field?.options || [], // Preserve all fields including ID
    prefillOption: PrefillOption.COUNTRY,
    sectionId: currentSectionId ?? "", // Current section of the field, default to "None"
  };

  const handleSubmit = async (values: Partial<EditCustomFieldFormInput>) => {
    if (!field) return;
    if (!Object.keys(values).length) {
      onHide();
      toast.info("No changes detected");
      return;
    }

    const hasNameChanges = "name" in values && values.name !== field.name;
    const hasSectionChanges = "sectionId" in values;
    const hasOptionsChanges = field.type === CustomFieldType.ENUMERATION && values.options;

    try {
      setIsSubmitting(true);

      let enumOptionsUpsert: Array<{ id?: string; label: string }> = [];
      let enumOptionsDelete: Array<{ id: string }> = [];

      // Only process enum options for enumeration fields
      if (hasOptionsChanges) {
        // Get original and new options
        const originalOptions = field.options || [];
        const newOptions = values.options || [];

        // Create a map of original options for easier lookup using ID
        const originalOptionsMap = new Map(
          originalOptions.map((option) => [option.id, option]),
        );

        // 1. Find NEW options (have no ID - these are newly added in the form)
        const newOptionsToCreate = newOptions.filter((newOption) => {
          // Check if this option has an ID property - if not, it's new
          return !newOption.id;
        });

        // 2. Find RENAMED options (exist in both by ID but have different labels)
        const renamedOptions = newOptions.filter((newOption) => {
          // Must have an ID to be an existing option
          if (!newOption.id) return false;

          const originalOption = originalOptionsMap.get(newOption.id);
          return originalOption && originalOption.label !== newOption.label;
        });

        // 3. Combine new + renamed for upsert
        enumOptionsUpsert = [
          // New options (no ID)
          ...newOptionsToCreate.map((option) => ({
            label: option.label,
          })),
          // Renamed options (with ID)
          ...renamedOptions.map((option) => ({
            id: option.id, // Use the option's database ID
            label: option.label,
          })),
        ];

        // 4. Find DELETED options (exist in originalOptions but not in newOptions by ID)
        const newOptionsIdSet = new Set(
          newOptions
            .filter((option) => option.id) // Only consider options with IDs
            .map((option) => option.id),
        );

        enumOptionsDelete = originalOptions
          .filter((originalOption) => !newOptionsIdSet.has(originalOption.id))
          .map((option) => ({
            id: option.id,
          }));
      }

      // Only make the API call if there are actual changes
      if (
        hasNameChanges ||
        enumOptionsUpsert.length > 0 ||
        enumOptionsDelete.length > 0 ||
        hasSectionChanges
      ) {
        // Check if there are deletions and show confirmation content
        if (enumOptionsDelete.length > 0 && !confirmationMode) {
          setPendingChanges({
            name: hasNameChanges ? values.name : undefined,
            enumOptionsUpsert,
            enumOptionsDelete,
            sectionId: hasSectionChanges ? values.sectionId : undefined,
          });
          setConfirmationMode(true);
          return; // Don't proceed yet, wait for confirmation
        }

        // Either no deletions or user already confirmed, proceed with API call
        const finalChanges = confirmationMode
          ? pendingChanges
          : {
              name: hasNameChanges ? values.name : undefined,
              enumOptionsUpsert,
              enumOptionsDelete,
              sectionId: hasSectionChanges ? values.sectionId : undefined,
            };
        if (!finalChanges) return;

        await editField({
          fieldId: field.id,
          name: finalChanges.name,
          enumOptionsUpsert: finalChanges.enumOptionsUpsert || [],
          enumOptionsDelete: finalChanges.enumOptionsDelete || [],
          sectionId: finalChanges.sectionId,
        });

        toast.success("Custom field updated successfully");
      } else {
        // No changes detected
        toast.info("No changes detected");
      }

      // Reset state and close modal
      setConfirmationMode(false);
      setPendingChanges(null);
      onHide();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update custom field";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    setConfirmationMode(false);
    setPendingChanges(null);
  };

  const handleConfirmDeletion = async () => {
    if (!field || !pendingChanges) return;

    try {
      setIsSubmitting(true);

      await editField({
        fieldId: field.id,
        name: pendingChanges.name,
        enumOptionsUpsert: pendingChanges.enumOptionsUpsert,
        enumOptionsDelete: pendingChanges.enumOptionsDelete,
        sectionId: pendingChanges.sectionId,
      });

      toast.success("Custom field updated successfully");

      // Reset state and close modal
      setConfirmationMode(false);
      setPendingChanges(null);
      onHide();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update custom field";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    // Reset all states when modal is closed
    setConfirmationMode(false);
    setPendingChanges(null);
    onHide();
  };

  // Component for confirmation content
  const ConfirmationContent = () => (
    <Flex flexDirection="column" gap="16px">
      <Flex alignItems="center" gap="12px">
        <Icon type="trash" size="large" />
        <Text variant="regular-xl">Confirm deletion of enumeration options</Text>
      </Flex>

      <Banner
        variant="warning"
        title="Impact Warning"
        description={
          pendingChanges
            ? `${pendingChanges.enumOptionsDelete.length} option${pendingChanges.enumOptionsDelete.length === 1 ? "" : "s"} will be permanently removed. These options will also be removed from all entities where they are currently used.`
            : "Options will be permanently removed."
        }
      />

      <Text variant="regular-md" color="neutral-700">
        This action cannot be undone. Are you sure you want to proceed?
      </Text>
    </Flex>
  );

  if (!field) return null;

  // If in confirmation mode, show a different modal with confirmation content
  if (confirmationMode) {
    return (
      <ModalV2 show={show} onHide={handleModalClose} title="Confirm Option Deletion">
        <ModalV2.Body>
          <ConfirmationContent />
        </ModalV2.Body>

        <ModalV2.Footer>
          <div className={styles.modalFooter}>
            <Button variant="neutral" onClick={handleGoBack} disabled={isSubmitting}>
              No, Reset changes and go back
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDeletion}
              disabled={isSubmitting}
              isLoading={isSubmitting}
              data-testid="confirm-deletion-button"
            >
              Yes, Proceed
            </Button>
          </div>
        </ModalV2.Footer>
      </ModalV2>
    );
  }

  // Normal edit mode
  return (
    <CustomFieldModal<EditCustomFieldFormInput>
      show={show}
      onHide={handleModalClose}
      title="Edit Custom Field"
      submitButtonText="Update Field"
      submitButtonTestId="edit-field-submit"
      isSubmitting={isSubmitting}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isEditMode={true}
      sections={sections}
    />
  );
};

export default EditCustomFieldModal;
